import { useCallback, useEffect, useState } from "react";

import { trpc } from "../trpc";
import {
  applyReminders,
  isSchedulingSupported,
  type ReminderPreferences,
  type ScheduleResult,
} from "./schedule";

export type RemindersStatus =
  | "loading"
  | "idle"
  | "saving"
  | "error";

interface UseStudyRemindersReturn {
  prefs: ReminderPreferences;
  status: RemindersStatus;
  // Last scheduling outcome — lets the UI explain "we couldn't because
  // you denied notifications", etc. Null until something's been applied.
  scheduleResult: ScheduleResult | null;
  error: string | null;
  supported: boolean;
  // Persist a full new preference object: writes server-side then
  // reconciles the on-device schedule from the canonical returned value.
  save: (next: ReminderPreferences) => Promise<void>;
}

const FALLBACK_PREFS: ReminderPreferences = {
  enabled: false,
  time: "18:00",
  days: [1, 2, 3, 4, 5],
};

// Drives the Settings → Notifications section. Loads the persisted
// preference on mount, reconciles the device schedule to it, and
// exposes a `save` that does the write-then-reschedule round-trip.
export function useStudyReminders(): UseStudyRemindersReturn {
  const [prefs, setPrefs] = useState<ReminderPreferences>(FALLBACK_PREFS);
  const [status, setStatus] = useState<RemindersStatus>("loading");
  const [scheduleResult, setScheduleResult] = useState<ScheduleResult | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      try {
        const server = await trpc.notifications.getPreferences.query();
        if (cancelled) return;
        setPrefs(server);
        // Reconcile the device to whatever the server says — covers the
        // reinstall case where the schedule is gone but the pref isn't.
        const result = await applyReminders(server);
        if (cancelled) return;
        setScheduleResult(result);
        setStatus("idle");
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : "couldn't load reminders",
        );
        setStatus("error");
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async (next: ReminderPreferences): Promise<void> => {
    setStatus("saving");
    setError(null);
    try {
      // Server is the source of truth + canonicalizes (dedup/sort days).
      const saved = await trpc.notifications.updatePreferences.mutate(next);
      setPrefs(saved);
      const result = await applyReminders(saved);
      setScheduleResult(result);
      setStatus("idle");
    } catch (err) {
      setError(err instanceof Error ? err.message : "couldn't save reminders");
      setStatus("error");
    }
  }, []);

  return {
    prefs,
    status,
    scheduleResult,
    error,
    supported: isSchedulingSupported(),
    save,
  };
}
